import { UseMutateAsyncFunction } from "@tanstack/react-query";
import { EditOutline18Icon } from "@vygruppen/spor-icon-react";
import { Stop } from "@vygruppen/vy-train-map";
import { ErrorBoundary } from "app/ErrorBoundary/ErrorBoundaryDashboard";
import {
  handleInternalMessageSubmit,
  useFormInternalMessage,
} from "features/CenterContent/RoleContent/InternalMessage/utils/useForm";
import { SubTypeInputs } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/stationEvents/subTypeInputs";
import { FormSchema } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/stationEvents/types/formSchema";
import { StationInfoRequest } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/stationEvents/types/requestSchema";
import { StationEventResponse } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/stationEvents/types/responseSchema";
import { formToStationInformationRequest } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/stationEvents/types/transformers";
import { getStopsTexts } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/stretchBuilderTexts";
import { FormFieldWithCustomInput } from "features/CenterContent/shared/operationalInformation/components/FormFieldWithCustomInput";
import {
  EventGroup,
  useOperationalInformationTypes,
} from "features/CenterContent/shared/operationalInformation/hooks/useOperationalInformationTypes";
import { FC, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ActionModal } from "shared/components/ActionModal";
import { FailureMessage } from "shared/components/feedback/FailureMessage/FailureMessage";
import {
  Alert,
  Box,
  DropsBadge,
  Flex,
  IconButton,
  Skeleton,
  SkeletonText,
  Stack,
  Text,
} from "shared/components/Spor";
import { TextTooltip } from "shared/components/TextTooltip";
import { getStationCustomInputs } from "shared/types/form";
import { AllOrNone } from "shared/utils/objectUtils";
import {
  StretchBuilderModalState,
  useStretchBuilder,
} from "stores/useStretchBuilder";

type DialogContentProps = {
  onFormSubmit: UseMutateAsyncFunction<
    StationEventResponse,
    Error,
    StationInfoRequest
  >;
  formSubmitStatus: "error" | "pending" | "idle" | "success";
};

export const BadgeText = ({
  selectedStops,
  allStopsSelected,
  stretchBuilderModalState,
  setStretchBuilderModalState,
}: {
  selectedStops: Stop[];
} & AllOrNone<{
  allStopsSelected: boolean;
  stretchBuilderModalState: StretchBuilderModalState;
  setStretchBuilderModalState: (value: StretchBuilderModalState) => void;
}>) => {
  const stopsTexts = getStopsTexts(selectedStops, allStopsSelected);
  return (
    <Flex gap="6px">
      <Text>
        {selectedStops.length < 2 ? "Stasjon: " : "Stasjoner: "}
        {stopsTexts.slice(0, 3).map((it) => (
          <DropsBadge
            key={it}
            semantics="success"
            marginX="3px"
            paddingTop="3px"
          >
            {it}
          </DropsBadge>
        ))}
        {stopsTexts.length > 3 && (
          <TextTooltip label={stopsTexts.slice(3).join(", ")}>
            <DropsBadge semantics="success" pt="0.5" ml="0.5">
              +{stopsTexts.length - 3}
            </DropsBadge>
          </TextTooltip>
        )}
      </Text>
      {setStretchBuilderModalState && (
        <IconButton
          size="xs"
          variant="tertiary"
          icon={<EditOutline18Icon />}
          onClick={() => {
            const newState = {
              ...stretchBuilderModalState,
              stretchBuilderMode: true,
            };
            setStretchBuilderModalState(newState);
          }}
          aria-label="Rediger stasjoner"
          width="fit-content"
          height="fit-content"
        />
      )}
    </Flex>
  );
};

export const StationEventModal: FC<DialogContentProps> = ({
  onFormSubmit,
  formSubmitStatus,
}) => {
  const { t } = useTranslation();

  const [
    selectedStops,
    setSelectedStops,
    allStopsSelected,
    closeAndResetStretchBuilder,
    stretchBuilderModalState,
    setStretchBuilderModalState,
    getStationEvent,
  ] = useStretchBuilder((state) => [
    state.selectedStops,
    state.setSelectedStops,
    state.allStopsSelected,
    state.closeAndResetStretchBuilder,
    state.stretchBuilderModalState,
    state.setStretchBuilderModalState,
    state.getStationEvent,
  ]);

  const modalState = stretchBuilderModalState.type;

  const { data: opInfoTypes, status: opInfoTypesStatus } =
    useOperationalInformationTypes(t);

  const formMethods = useFormContext<FormSchema>();
  const type = formMethods.watch("stationForm.stationInformationType");

  const prevStationEvent = getStationEvent();
  const isEditMode = !!prevStationEvent;

  const internalMessagesFormMethods = useFormInternalMessage(
    prevStationEvent?.internalMessages,
  );
  const onInternalMessagesSubmit = handleInternalMessageSubmit(
    internalMessagesFormMethods,
  );

  useEffect(() => {
    if (selectedStops.length <= 0) {
      const stop = formMethods.getValues("stationForm.stop");
      const stops = formMethods.getValues("stationForm.stops");
      if (stop !== undefined) {
        setSelectedStops([stop]);
      } else if (stops.length > 0) {
        setSelectedStops(stops);
      }
    }
  }, []);

  useEffect(() => {
    if (selectedStops.length > 0) {
      formMethods.setValue("stationForm.stops", selectedStops);
    }
  }, [selectedStops]);

  useEffect(() => {
    if (type) {
      formMethods.clearErrors("stationForm");
    }
  }, [type]);

  if (modalState !== "station") return null;

  return (
    <ActionModal
      title={`${isEditMode ? t("Endre") : t("Opprett")} ${t("stasjonshendelse")}`}
      actionTitle={`${isEditMode ? t("Endre") : t("Opprett")} ${t("hendelse")}`}
      onClose={closeAndResetStretchBuilder}
      onSubmit={formMethods.handleSubmit((data) =>
        onFormSubmit(formToStationInformationRequest(data)).then((response) =>
          onInternalMessagesSubmit(response, prevStationEvent?.uuid),
        ),
      )}
      loading={formSubmitStatus === "pending"}
      isSuccess={formSubmitStatus === "success"}
      isError={formSubmitStatus === "error"}
      successMessage={
        isEditMode ? t("Hendelse endret") : t("Hendelse opprettet")
      }
      failureMessage={`${
        isEditMode
          ? t("Kunne ikke endre hendelse.")
          : t("Kunne ikke opprette hendelse.")
      } ${t("Prøv på nytt, eller kontakt IT hvis feilen vedvarer.")}`}
      internalMessagesFormMethods={internalMessagesFormMethods}
    >
      <ErrorBoundary>
        <Box w="100%" display="grid" gap="18px">
          <BadgeText
            selectedStops={selectedStops}
            allStopsSelected={allStopsSelected}
            stretchBuilderModalState={stretchBuilderModalState}
            setStretchBuilderModalState={setStretchBuilderModalState}
          />
          {opInfoTypesStatus === "pending" && (
            <>
              <Stack gap="12px">
                <Skeleton height="36px" />
                <Skeleton height="36px" />
              </Stack>
              <SkeletonText lineClamp="3" width="30%" />
            </>
          )}

          {opInfoTypesStatus === "error" && <FailureMessage />}

          {opInfoTypesStatus === "success" && (
            <>
              <FormFieldWithCustomInput<FormSchema>
                items={opInfoTypes.events
                  .filter((_event) => _event.group === EventGroup.STATION)
                  .toSorted((a, b) => a.label.localeCompare(b.label))}
                {...getStationCustomInputs(t).event}
              />

              {type && <SubTypeInputs type={type} />}

              {type === "STATION_GENERAL" && (
                <Alert data-color="info" id="infoBoxActionModal">
                  {t(
                    "Hendelsen inneholder egendefinert tekst. Sjekk at beskrivelsen ser riktig ut før du går videre.",
                  )}
                </Alert>
              )}
            </>
          )}
        </Box>
      </ErrorBoundary>
    </ActionModal>
  );
};
